import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { CreateEventMutationFn, CreateEventMutation } from '../routes/CreateWeddingForm/schema.graphql.generated';
import { useCreateEventMutation } from '../routes/CreateWeddingForm/schema.graphql.generated';
import type { ApolloError, FetchResult } from '@apollo/client';
import { addError } from '@shared/utils/logger';
import { recordCreateWeddingFlowEvent } from '@apps/acquisitionFunnels/createWedding/createWeddingFlowEvents';
import { classifyCreateEventError } from '@apps/acquisitionFunnels/createWedding/utils/classifyCreateEventError';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';

type EventPromise = Nullable<Promise<FetchResult<CreateEventMutation>>>;

interface EventListenerCallbackExtraParams {}

type EventListenerCallback<T> = (mutation: T, extraParams: EventListenerCallbackExtraParams) => Promise<unknown>;

interface EventStateContext<T, R> {
  createEvent: T;
  createdEventData?: Nullable<R>;
  isEventCreationSettled: boolean;
  createError?: ApolloError;
  addEventCreatedListener: (cb: EventListenerCallback<R>) => Promise<void>;
}

interface PendingEventListeners {
  cb: EventListenerCallback<CreateEventMutation>;
  inProgress: boolean;
}

type WeddingStateContext = EventStateContext<CreateEventMutationFn, CreateEventMutation>;

const [Provider, useEventState] = createContext<WeddingStateContext>({ name: 'WeddingState' });

const EventStateProvider: React.FC = ({ children }) => {
  const [mutation, { data: createdEventData, called, loading, error }] = useCreateEventMutation();
  const [pendingEventListeners, setPendingEventListeners] = useState<Set<PendingEventListeners>>(new Set());
  const eventPromise = useRef<EventPromise>(null);

  useEffect(() => {
    if (!loading && createdEventData) {
      pendingEventListeners.forEach(eventListener => {
        if (!eventListener.inProgress) {
          eventListener.inProgress = true;
          eventListener.cb(createdEventData, {}).finally(() => {
            setPendingEventListeners(prev => {
              prev.delete(eventListener);
              return new Set(prev);
            });
          });
        }
      });
    }
  }, [createdEventData, loading, pendingEventListeners]);

  const createEvent: CreateEventMutationFn = useCallback(
    options => {
      const promise = mutation({
        ...options,
        onError: error => {
          // Clear stale listeners from previous failed attempts so they don't fire on retry success
          setPendingEventListeners(new Set());

          const errorType = classifyCreateEventError(error);
          const networkDetails = parseNetworkErrorDetails(error);

          if (errorType === 'rateLimit') {
            addError('TOO_MANY_REQUESTS', options?.variables?.payload);
          }
          addError(error, options?.variables?.payload);
          recordCreateWeddingFlowEvent({
            action: 'EventCreateFailed',
            errorMessage: error?.message ?? 'unknown',
            errorType,
            statusCode: networkDetails.statusCode ?? null,
            isTimeout: networkDetails.isTimeout,
            isHtmlResponse: networkDetails.isHtmlResponse,
          });
        },
      });

      eventPromise.current = promise;

      return promise;
    },
    [mutation],
  );

  const addEventCreatedListener = useCallback(async (cb: EventListenerCallback<CreateEventMutation>) => {
    setPendingEventListeners(prev => {
      prev.add({ cb, inProgress: false });
      return new Set(prev);
    });
  }, []);

  const isEventCreationSettled = called && !error && !loading && pendingEventListeners.size === 0;

  return <Provider value={{ createEvent, addEventCreatedListener, isEventCreationSettled, createdEventData, createError: error }}>{children}</Provider>;
};

export { useEventState, EventStateProvider };
